import { Injectable } from "@angular/core";
import {
	BlockOffsetSelector,
	EMPTY_PROTOCOL_NOTE_ANCHOR,
	ProtocolNoteAnchor,
	ProtocolNoteTextAnchor,
	TextQuoteSelector,
	isTextQuoteAnchor,
} from "./entities/protocol-note-anchor";

/** Une position normalisée qui vient réellement du DOM (les séparateurs synthétiques valent null). */
interface TextIndexEntry {
	node: Text;
	offset: number;
}

interface TextIndex {
	text: string;
	map: (TextIndexEntry | null)[];
}

const SOFT_HYPHEN = "\u00AD";
const NBSP = "\u00A0";

/**
 * `Selection.toString()` insère un saut de ligne aux frontières de bloc là où le
 * DOM n'a aucun nœud texte. On injecte donc un séparateur au même endroit, sinon
 * une sélection qui traverse deux paragraphes ne se retrouve jamais.
 */
const BLOCK_TAGS = new Set([
	"ADDRESS",
	"ARTICLE",
	"ASIDE",
	"BLOCKQUOTE",
	"BR",
	"DD",
	"DIV",
	"DL",
	"DT",
	"FIELDSET",
	"FIGCAPTION",
	"FIGURE",
	"FOOTER",
	"FORM",
	"H1",
	"H2",
	"H3",
	"H4",
	"H5",
	"H6",
	"HEADER",
	"HR",
	"LI",
	"MAIN",
	"NAV",
	"OL",
	"P",
	"PRE",
	"SECTION",
	"TABLE",
	"TBODY",
	"TD",
	"TFOOT",
	"TH",
	"THEAD",
	"TR",
	"UL",
]);

@Injectable({
	providedIn: "root",
})
export class ProtocolAnchorService {
	static readonly HIGHLIGHT_CLASS = "mitp-note-highlight";
	static readonly MARKER_CLASS = "mitp-note-marker";
	static readonly NOTE_ID_ATTRIBUTE = "data-mitp-note-id";

	private static readonly CONTEXT_CHARS = 60;

	/**
	 * Normalise une chaîne exactement comme l'index construit sur le DOM, pour
	 * que le texte stocké dans l'ancre puisse être recherché tel quel.
	 *
	 * La transformation est volontairement appliquée caractère par caractère et
	 * ne produit jamais plus d'un caractère en sortie : c'est cette propriété qui
	 * permet de remonter d'une position normalisée jusqu'au nœud texte d'origine.
	 * On n'applique pas de normalisation Unicode (NFC) : l'aiguille et la meule
	 * de foin proviennent toutes deux du même DOM, les composer différemment
	 * ferait plus de mal que de bien.
	 */
	normalizeText(value: string): string {
		let out = "";
		for (const char of value ?? "") {
			if (char === SOFT_HYPHEN) {
				continue;
			}
			if (char === NBSP || /\s/.test(char)) {
				if (out.length > 0 && !out.endsWith(" ")) {
					out += " ";
				}
				continue;
			}
			out += char;
		}
		return out.trim();
	}

	/**
	 * Construit une ancre à partir de la sélection courante.
	 *
	 * `win` est la fenêtre qui porte la sélection : le contenu des protocoles est
	 * rendu dans une iframe, la sélection ne se trouve donc pas dans le document
	 * hôte.
	 */
	buildAnchorFromSelection(
		containerEl: HTMLElement,
		win: Window,
		contextChars: number = ProtocolAnchorService.CONTEXT_CHARS
	): { anchor: ProtocolNoteAnchor; selectedText: string } {
		const sel = win?.getSelection();
		if (!sel || sel.rangeCount === 0 || sel.isCollapsed) {
			return { anchor: EMPTY_PROTOCOL_NOTE_ANCHOR, selectedText: "" };
		}

		const range = sel.getRangeAt(0);
		if (!containerEl.contains(range.commonAncestorContainer)) {
			return { anchor: EMPTY_PROTOCOL_NOTE_ANCHOR, selectedText: "" };
		}

		const selectedText = this.normalizeText(sel.toString());
		if (!selectedText) {
			return { anchor: EMPTY_PROTOCOL_NOTE_ANCHOR, selectedText: "" };
		}

		// Le préfixe et le suffixe lèvent l'ambiguïté quand le texte apparaît
		// plusieurs fois dans le protocole.
		const index = this.buildTextIndex(containerEl);
		const bounds = this.rangeToNormalizedBounds(index, range);

		let prefix = "";
		let suffix = "";
		if (bounds) {
			prefix = this.normalizeText(index.text.slice(Math.max(0, bounds.start - contextChars), bounds.start));
			suffix = this.normalizeText(index.text.slice(bounds.end, bounds.end + contextChars));
		}

		const anchor: ProtocolNoteTextAnchor = {
			type: "TextQuoteSelector",
			exact: selectedText,
			prefix: prefix,
			suffix: suffix,
		};

		const secondary = this.buildBlockOffset(containerEl, range, selectedText);
		if (secondary) {
			anchor.secondary = secondary;
		}

		return { anchor: anchor, selectedText: selectedText };
	}

	/**
	 * Retrouve l'ancre dans le contenu actuellement rendu.
	 * Renvoie `null` quand le texte a disparu — cas attendu sur une note périmée.
	 */
	resolveAnchor(containerEl: HTMLElement, anchor: ProtocolNoteAnchor): Range | null {
		if (!containerEl || !isTextQuoteAnchor(anchor)) {
			return null;
		}

		const index = this.buildTextIndex(containerEl);
		const needle = this.normalizeText(anchor.exact);
		if (!needle) {
			return null;
		}

		const occurrences: number[] = [];
		let from = index.text.indexOf(needle);
		while (from !== -1) {
			occurrences.push(from);
			from = index.text.indexOf(needle, from + 1);
		}

		if (occurrences.length === 0) {
			return this.resolveBlockOffset(containerEl, anchor.secondary);
		}

		const start =
			occurrences.length === 1 ? occurrences[0] : this.bestOccurrence(index.text, occurrences, needle, anchor);

		return this.boundsToRange(index, start, start + needle.length);
	}

	/**
	 * Entoure le passage ancré d'un `<mark>` et pose un marqueur cliquable juste
	 * après. Renvoie `false` quand l'ancre n'a pas pu être retrouvée.
	 */
	highlight(containerEl: HTMLElement, anchor: ProtocolNoteAnchor, noteId: number): boolean {
		const range = this.resolveAnchor(containerEl, anchor);
		if (!range) {
			return false;
		}

		const doc = containerEl.ownerDocument;
		const marks = this.wrapRange(range, doc, noteId);
		if (marks.length === 0) {
			return false;
		}

		const last = marks[marks.length - 1];
		last.parentNode?.insertBefore(this.createMarker(doc, noteId), last.nextSibling);
		return true;
	}

	/** Défait tout ce que `highlight` a posé, pour pouvoir re-rendre proprement. */
	clearHighlights(containerEl: HTMLElement): void {
		if (!containerEl) {
			return;
		}

		containerEl
			.querySelectorAll(`.${ProtocolAnchorService.MARKER_CLASS}`)
			.forEach((marker) => marker.parentNode?.removeChild(marker));

		containerEl.querySelectorAll(`.${ProtocolAnchorService.HIGHLIGHT_CLASS}`).forEach((mark) => {
			const parent = mark.parentNode;
			if (!parent) {
				return;
			}
			while (mark.firstChild) {
				parent.insertBefore(mark.firstChild, mark);
			}
			parent.removeChild(mark);
			// Refusionne les nœuds texte scindés par le surlignage, sans quoi les
			// index construits ensuite ne correspondraient plus au contenu initial.
			(parent as Element).normalize?.();
		});
	}

	/** Le premier `<mark>` de la note, pour pouvoir y faire défiler la vue. */
	findHighlight(containerEl: HTMLElement, noteId: number): HTMLElement | null {
		return containerEl?.querySelector(
			`.${ProtocolAnchorService.HIGHLIGHT_CLASS}[${ProtocolAnchorService.NOTE_ID_ATTRIBUTE}="${noteId}"]`
		);
	}

	private createMarker(doc: Document, noteId: number): HTMLElement {
		const marker = doc.createElement("span");
		marker.className = ProtocolAnchorService.MARKER_CLASS;
		marker.setAttribute(ProtocolAnchorService.NOTE_ID_ATTRIBUTE, String(noteId));
		marker.setAttribute("role", "button");
		marker.setAttribute("tabindex", "0");
		// Les polices d'icônes Material ne sont pas chargées dans l'iframe : le
		// SVG inline évite de dépendre d'une ressource externe.
		marker.innerHTML =
			'<svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" focusable="false">' +
			'<path fill="currentColor" d="M19 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h9l7-7V5a2 2 0 0 0-2-2zm-6 15v-5h5l-5 5z"/>' +
			"</svg>";
		return marker;
	}

	/**
	 * Aplatit le contenu rendu en une chaîne normalisée, en gardant pour chaque
	 * caractère le nœud texte et l'offset dont il provient.
	 */
	private buildTextIndex(root: HTMLElement): TextIndex {
		const doc = root.ownerDocument;
		const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT);

		let text = "";
		const map: (TextIndexEntry | null)[] = [];
		let pendingSeparator = false;
		let node: Node | null;

		const pushChar = (char: string, entry: TextIndexEntry | null): void => {
			text += char;
			map.push(entry);
		};

		while ((node = walker.nextNode())) {
			if (node.nodeType === Node.ELEMENT_NODE) {
				if (BLOCK_TAGS.has((node as Element).tagName)) {
					pendingSeparator = true;
				}
				continue;
			}

			const data = (node as Text).data ?? "";
			for (let i = 0; i < data.length; i++) {
				const char = data[i];
				if (char === SOFT_HYPHEN) {
					continue;
				}

				if (char === NBSP || /\s/.test(char)) {
					pendingSeparator = true;
					continue;
				}

				if (pendingSeparator) {
					pendingSeparator = false;
					if (text.length > 0 && !text.endsWith(" ")) {
						pushChar(" ", null);
					}
				}

				pushChar(char, { node: node as Text, offset: i });
			}
		}

		return { text: text, map: map };
	}

	/** Position normalisée d'un Range DOM, en s'appuyant sur l'index déjà construit. */
	private rangeToNormalizedBounds(index: TextIndex, range: Range): { start: number; end: number } | null {
		let start = -1;
		let end = -1;

		for (let i = 0; i < index.map.length; i++) {
			const entry = index.map[i];
			if (!entry) {
				continue;
			}
			if (!range.intersectsNode(entry.node)) {
				continue;
			}

			const afterStart =
				entry.node !== range.startContainer ||
				range.startOffset === undefined ||
				entry.offset >= range.startOffset;
			const beforeEnd =
				entry.node !== range.endContainer || range.endOffset === undefined || entry.offset < range.endOffset;

			if (afterStart && beforeEnd) {
				if (start === -1) {
					start = i;
				}
				end = i + 1;
			}
		}

		return start === -1 ? null : { start: start, end: end };
	}

	/**
	 * Choisit l'occurrence dont le contexte colle le mieux à celui enregistré.
	 * Le préfixe est comparé par la fin, le suffixe par le début.
	 */
	private bestOccurrence(haystack: string, occurrences: number[], needle: string, anchor: TextQuoteSelector): number {
		const prefix = this.normalizeText(anchor.prefix ?? "");
		const suffix = this.normalizeText(anchor.suffix ?? "");

		let best = occurrences[0];
		let bestScore = -1;

		for (const occurrence of occurrences) {
			const before = haystack.slice(Math.max(0, occurrence - prefix.length), occurrence);
			const after = haystack.slice(occurrence + needle.length, occurrence + needle.length + suffix.length);
			const score = this.commonSuffixLength(before, prefix) + this.commonPrefixLength(after, suffix);
			if (score > bestScore) {
				bestScore = score;
				best = occurrence;
			}
		}

		return best;
	}

	private commonPrefixLength(a: string, b: string): number {
		let i = 0;
		while (i < a.length && i < b.length && a[i] === b[i]) {
			i++;
		}
		return i;
	}

	private commonSuffixLength(a: string, b: string): number {
		let i = 0;
		while (i < a.length && i < b.length && a[a.length - 1 - i] === b[b.length - 1 - i]) {
			i++;
		}
		return i;
	}

	private buildBlockOffset(
		containerEl: HTMLElement,
		range: Range,
		selectedText: string
	): BlockOffsetSelector | undefined {
		const node = range.commonAncestorContainer;
		const element = (node.nodeType === Node.ELEMENT_NODE ? node : node.parentElement) as Element | null;
		const block = element?.closest("[id], [data-id]");
		if (!block || !containerEl.contains(block)) {
			return undefined;
		}

		const blockId = block.getAttribute("id") ?? block.getAttribute("data-id");
		if (!blockId) {
			return undefined;
		}

		const blockIndex = this.buildTextIndex(block as HTMLElement);
		const position = blockIndex.text.indexOf(selectedText);
		if (position === -1) {
			return undefined;
		}

		return {
			type: "BlockOffset",
			blockId: blockId,
			offsetStart: position,
			offsetEnd: position + selectedText.length,
		};
	}

	private resolveBlockOffset(containerEl: HTMLElement, secondary: BlockOffsetSelector | undefined): Range | null {
		if (!secondary?.blockId) {
			return null;
		}

		const block =
			containerEl.querySelector(`[id="${secondary.blockId}"]`) ??
			containerEl.querySelector(`[data-id="${secondary.blockId}"]`);
		if (!block) {
			return null;
		}

		const index = this.buildTextIndex(block as HTMLElement);
		if (secondary.offsetStart >= index.map.length || secondary.offsetEnd > index.map.length) {
			return null;
		}

		return this.boundsToRange(index, secondary.offsetStart, secondary.offsetEnd);
	}

	/**
	 * Reconvertit des positions normalisées en Range DOM. Les bornes sont
	 * ramenées sur le caractère réel le plus proche, pour ignorer les
	 * séparateurs synthétiques insérés aux frontières de bloc.
	 */
	private boundsToRange(index: TextIndex, start: number, end: number): Range | null {
		let first: TextIndexEntry | null = null;
		for (let i = start; i < end && i < index.map.length; i++) {
			if (index.map[i]) {
				first = index.map[i];
				break;
			}
		}

		let last: TextIndexEntry | null = null;
		for (let i = Math.min(end, index.map.length) - 1; i >= start; i--) {
			if (index.map[i]) {
				last = index.map[i];
				break;
			}
		}

		if (!first || !last) {
			return null;
		}

		const range = first.node.ownerDocument.createRange();
		range.setStart(first.node, first.offset);
		range.setEnd(last.node, last.offset + 1);
		return range;
	}

	/** Entoure chaque fragment de texte couvert par le Range d'un `<mark>`. */
	private wrapRange(range: Range, doc: Document, noteId: number): HTMLElement[] {
		const targets = this.textNodesInRange(range, doc);
		const marks: HTMLElement[] = [];

		for (const target of targets) {
			let node = target.node;
			if (target.end < node.data.length) {
				node.splitText(target.end);
			}
			if (target.start > 0) {
				node = node.splitText(target.start);
			}

			const mark = doc.createElement("mark");
			mark.className = ProtocolAnchorService.HIGHLIGHT_CLASS;
			mark.setAttribute(ProtocolAnchorService.NOTE_ID_ATTRIBUTE, String(noteId));
			node.parentNode?.insertBefore(mark, node);
			mark.appendChild(node);
			marks.push(mark);
		}

		return marks;
	}

	/**
	 * Les nœuds texte couverts, relevés avant toute modification : `splitText`
	 * réécrit l'arbre, itérer en même temps qu'on le découpe serait faux.
	 */
	private textNodesInRange(range: Range, doc: Document): { node: Text; start: number; end: number }[] {
		const root =
			range.commonAncestorContainer.nodeType === Node.ELEMENT_NODE
				? (range.commonAncestorContainer as Element)
				: range.commonAncestorContainer.parentElement;
		if (!root) {
			return [];
		}

		const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT);
		const targets: { node: Text; start: number; end: number }[] = [];
		let node: Node | null;

		while ((node = walker.nextNode())) {
			const text = node as Text;
			if (!range.intersectsNode(text)) {
				continue;
			}

			const start = text === range.startContainer ? range.startOffset : 0;
			const end = text === range.endContainer ? range.endOffset : text.data.length;
			if (end > start) {
				targets.push({ node: text, start: start, end: end });
			}
		}

		return targets;
	}
}
